import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Check, Copy, Ellipsis, History, Info, KeyRound, LayoutList, Lock, RefreshCw, ScrollText, Shield, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";
import FooterNav from "../components/FooterNav";
import { deleteSpace, fetchSpaceAuthority, getSpace, joinSpace, leaveSpace, requestSpaceDissolution, reviewSpaceDissolution, unlockSpace, updateSpace, type Space, type SpaceAuthority } from "../api/spaces";
import { connectSocket, getSocket } from "../api/socket";
import { formatBlackspaceDateTime } from "../utils/time";
import { forgetSpaceFromListMemory } from "../features/spaces/listMemory";
import SpaceAbout from "../features/spaces/SpaceAbout";
import { hasSpacesBackState, readSpacesBackState, type SpacesNavigationState } from "../features/spaces/navigation";
import { hasIdentity, requireIdentity } from "../utils/identityGate";

const PostsTab = lazy(() => import("../components/PostsTab"));
const MembersTab = lazy(() => import("../components/MembersTab"));
const EventsTab = lazy(() => import("../components/EventsTab"));
const MaterialsTab = lazy(() => import(/* webpackChunkName: "space-materials" */ "../components/MaterialsTab"));
const ModerationTab = lazy(() => import("../components/SpaceModerationTab"));

const baseTabs = [
    { id: "posts", label: "Posts", icon: LayoutList },
    { id: "events", label: "Events", icon: CalendarDays },
    { id: "library", label: "Library", icon: ScrollText },
    { id: "info", label: "Info", icon: Info },
] as const;
type SpaceTab = "posts" | "events" | "library" | "info" | "moderation" | "manage";

function isSpaceTab(value: string | null): value is SpaceTab {
    return value === "posts" || value === "events" || value === "library" || value === "info" || value === "moderation" || value === "manage";
}

const SPACE_ACCENTS = [
    "#22d3ee",
    "#14b8a6",
    "#34d399",
    "#a3e635",
    "#facc15",
    "#fb923c",
    "#f43f5e",
    "#ec4899",
    "#a855f7",
    "#6366f1",
    "#60a5fa",
    "#94a3b8",
];
const MIN_UNLOCK_CODE_LENGTH = 1;

function TabLoader() {
    return (
        <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-28 animate-pulse rounded-lg border border-neutral-800 bg-neutral-950" />
            ))}
        </div>
    );
}

function JoinRequiredPanel({ title }: { title: string }) {
    return (
        <div className="bs-space-post-notice bs-space-member-gate rounded-2xl border border-white/10 bg-neutral-950 p-4 text-sm text-neutral-400">
            Join this Space to see {title}.
        </div>
    );
}

function accessLanguage(mode = "open") {
    if (mode === "private") {
        return {
            label: "Invite only",
            gateTitle: "You have reached an invitation room",
            gateBadge: "Invitation required",
            gateCopy: "Enter the invite code to join this space.",
            codeLabel: "Invite code",
            noteLabel: "Invite note",
            manageCopy: "Share the invite code or invite link with the identities you want inside.",
            copied: "Invite code copied",
            copiedLink: "Invite link copied",
            action: "Join with invite",
        };
    }
    if (mode === "sealed") {
        return {
            label: "Sealed",
            gateTitle: "This space is sealed",
            gateBadge: "Sealed room",
            gateCopy: "Only authority can open this room.",
            codeLabel: "Access code",
            noteLabel: "Seal note",
            manageCopy: "This room is sealed by authority.",
            copied: "Access code copied",
            copiedLink: "Access link copied",
            action: "Open",
        };
    }
    if (mode === "locked") {
        return {
            label: "Locked",
            gateTitle: "This space is locked",
            gateBadge: "Locked room",
            gateCopy: "Enter the lock code to open this space.",
            codeLabel: "Lock code",
            noteLabel: "Lock note",
            manageCopy: "Share the lock code with identities allowed to open this door.",
            copied: "Lock code copied",
            copiedLink: "Lock link copied",
            action: "Unlock",
        };
    }
    return {
        label: "Open",
        gateTitle: "This space is open",
        gateBadge: "Open room",
        gateCopy: "Join the room to enter.",
        codeLabel: "Code",
        noteLabel: "Note",
        manageCopy: "Anyone can find and join this room.",
        copied: "Code copied",
        copiedLink: "Link copied",
        action: "Enter",
    };
}

function toDateTimeLocal(value?: string | null) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatWhen(value?: string | null) {
    return formatBlackspaceDateTime(value, "Never");
}

function cleanAccentColor(value?: string) {
    return /^#[0-9a-fA-F]{6}$/.test(value || "") ? String(value).toLowerCase() : "#22d3ee";
}

function alphaHex(hex: string, alpha: string) {
    return `${cleanAccentColor(hex)}${alpha}`;
}

function spaceUnlockErrorMessage(message?: string) {
    const clean = String(message || "").trim();
    const lower = clean.toLowerCase();
    if (!clean) return "Could not unlock this space.";
    if (lower.includes("too many")) return "Too many attempts. Please wait a moment.";
    if (lower.includes("archived")) return "This Space is archived and cannot be unlocked.";
    if (lower.includes("banned")) return "This identity cannot enter this Space.";
    if (lower.includes("sealed")) return "This Space cannot be opened from here.";
    if (lower.includes("door did not open") || lower.includes("invalid") || lower.includes("expired")) {
        return "Incorrect code. Try again.";
    }
    return clean;
}

export default function SpaceDetail() {
    const { spaceId } = useParams<{ spaceId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isAnonymous = !hasIdentity();
    const routeState = location.state as SpacesNavigationState | null;
    const backTarget = readSpacesBackState(location.state);
    const accessCodeFromQuery = new URLSearchParams(location.search).get("code") || "";
    const [space, setSpace] = useState<Space | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const requestedTab = new URLSearchParams(location.search).get("tab");
    const [activeTab, setActiveTab] = useState<SpaceTab>(isSpaceTab(requestedTab) ? requestedTab : "posts");
    const mainRef = useRef<HTMLElement | null>(null);
    const [joining, setJoining] = useState(false);
    const [authority, setAuthority] = useState<SpaceAuthority | null>(null);
    const [inviteCode, setInviteCode] = useState("");
    const [revealedAccessCode, setRevealedAccessCode] = useState(() => {
        return routeState?.revealedAccessCode || "";
    });
    const [moreOpen, setMoreOpen] = useState(false);
    const [spaceActionBusy, setSpaceActionBusy] = useState(false);
    const [accessModeDraft, setAccessModeDraft] = useState("open");
    const [lockedReasonDraft, setLockedReasonDraft] = useState("");
    const [continuityOpen, setContinuityOpen] = useState(false);
    const [dissolutionReason, setDissolutionReason] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [reviewNote, setReviewNote] = useState("");
    const [copiedAccess, setCopiedAccess] = useState<"code" | "link" | null>(null);
    const [inviteExpiresAtDraft, setInviteExpiresAtDraft] = useState("");
    const [revokeExistingAccessDraft, setRevokeExistingAccessDraft] = useState(false);
    const [titleDraft, setTitleDraft] = useState("");
    const [descriptionDraft, setDescriptionDraft] = useState("");
    const [accentColorDraft, setAccentColorDraft] = useState("#22d3ee");
    const [settingsNotice, setSettingsNotice] = useState("");

    function syncSpaceDrafts(data: Space) {
        setAccessModeDraft(data.accessMode || "open");
        setLockedReasonDraft(data.lockedReason || "");
        setInviteExpiresAtDraft(toDateTimeLocal(data.inviteExpiresAt));
        setTitleDraft(data.title || "");
        setDescriptionDraft(data.desc || "");
        setAccentColorDraft(cleanAccentColor(data.accentColor));
    }

    function clearLifecycleDrafts() {
        setDissolutionReason("");
        setReviewNote("");
        setDeleteConfirm(false);
    }

    function closeSettings() {
        setSettingsNotice("");
        clearLifecycleDrafts();
    }

    function closeContinuity() {
        setContinuityOpen(false);
        clearLifecycleDrafts();
    }

    useEffect(() => {
        let alive = true;
        async function loadSpace() {
            if (!spaceId) return;
            setLoading(true);
            setError(null);
            try {
                const data = await getSpace(spaceId);
                if (alive) {
                    setSpace(data);
                    syncSpaceDrafts(data);
                    if (data.inviteCode) setRevealedAccessCode(data.inviteCode);
                    if (accessCodeFromQuery) setInviteCode(accessCodeFromQuery.toUpperCase().slice(0, 40));
                }
            } catch (err: any) {
                if (alive) {
                    if (String(err?.message || '').toLowerCase().includes('space not found') && spaceId) {
                        forgetSpaceFromListMemory(spaceId);
                    }
                    setError(err?.message || "Failed to load space.");
                }
            } finally {
                if (alive) setLoading(false);
            }
        }

        loadSpace();
        return () => {
            alive = false;
        };
    }, [accessCodeFromQuery, spaceId]);

    useEffect(() => {
        const tab = new URLSearchParams(location.search).get("tab");
        setActiveTab(isSpaceTab(tab) ? tab : "posts");
        mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, [location.search]);

    useEffect(() => {
        if (!continuityOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setContinuityOpen(false);
                setDissolutionReason("");
                setReviewNote("");
            }
        };
        window.addEventListener("keydown", closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [continuityOpen]);

    useEffect(() => {
        if (!error || !space) return;
        const timer = window.setTimeout(() => setError(null), 4200);
        return () => window.clearTimeout(timer);
    }, [error, space]);

    function selectTab(tab: SpaceTab) {
        const params = new URLSearchParams(location.search);
        if (tab === "posts") params.delete("tab");
        else params.set("tab", tab);
        closeSettings();
        navigate(
            { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" },
            { state: location.state }
        );
    }

    function goBackToSource() {
        if (!hasSpacesBackState(location.state) && window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate(backTarget.path, { replace: true });
    }

    useEffect(() => {
        let alive = true;
        async function loadAuthority() {
            if (!spaceId) return;
            try {
                const data = await fetchSpaceAuthority(spaceId);
                if (alive) setAuthority(data);
            } catch {
                if (alive) setAuthority(null);
            }
        }
        loadAuthority();
        return () => {
            alive = false;
        };
    }, [spaceId]);

    useEffect(() => {
        if (!space?.id || space.canView === false) return;

        let socket = getSocket();
        try {
            socket = socket || connectSocket();
        } catch (err) {
            console.error("Space presence socket failed:", err);
            return;
        }

        const handlePresence = (payload: { spaceId: string; online: string | number }) => {
            if (payload.spaceId !== space.id) return;
            setSpace((prev) => prev ? { ...prev, online: String(payload.online) } : prev);
        };

        socket.emit("space:join", { spaceId: space.id });
        socket.on("space:presence", handlePresence);

        return () => {
            socket?.off("space:presence", handlePresence);
            socket?.emit("space:leave", { spaceId: space.id });
        };
    }, [space?.id, space?.canView]);

    useEffect(() => {
        if (!settingsNotice) return;
        const timer = window.setTimeout(() => setSettingsNotice(""), 2200);
        return () => window.clearTimeout(timer);
    }, [settingsNotice]);

    async function toggleMembership() {
        if (!space) return;
        if (isAnonymous) return void requireIdentity(space.isJoined ? "leave this Space" : "join this Space");
        if (space.isArchived) return;
        if (authority?.isPresence || (space.canManage && !space.isJoined)) return;
        if (authority?.isCreator && space.isJoined) {
            setError(null);
            clearLifecycleDrafts();
            setContinuityOpen(true);
            return;
        }
        setJoining(true);
        setError(null);
        try {
            const updated = space.isJoined ? await leaveSpace(space.id) : await joinSpace(space.id);
            setSpace(updated);
        } catch (err: any) {
            setError(err?.message || "Could not update membership.");
        } finally {
            setJoining(false);
        }
    }

    async function handleDissolutionRequest() {
        if (!space || dissolutionReason.trim().length < 12) return;
        setSpaceActionBusy(true);
        setError(null);
        try {
            const result = await requestSpaceDissolution(space.id, dissolutionReason.trim());
            if (result.space) setSpace(result.space);
            setSettingsNotice(result.message);
            closeContinuity();
            setDissolutionReason("");
            if (result.archived) navigate(`/spaces/${space.id}`);
            else {
                const refreshed = await getSpace(space.id);
                setSpace(refreshed);
            }
        } catch (err: any) {
            setError(err?.message || "Could not request dissolution.");
        } finally {
            setSpaceActionBusy(false);
        }
    }

    async function handleDeleteSpace() {
        if (!space?.canDeleteDirectly) return;
        if (!deleteConfirm) {
            setDeleteConfirm(true);
            return;
        }
        setSpaceActionBusy(true);
        setError(null);
        try {
            const result = await deleteSpace(space.id);
            setSettingsNotice(result.message);
            closeSettings();
            closeContinuity();
            navigate("/spaces");
        } catch (err: any) {
            setError(err?.message || "Could not delete this Space.");
        } finally {
            setSpaceActionBusy(false);
        }
    }

    async function handleDissolutionReview(decision: "approved" | "rejected") {
        if (!space?.dissolutionRequest?.id) return;
        setSpaceActionBusy(true);
        setError(null);
        try {
            const result = await reviewSpaceDissolution(space.id, space.dissolutionRequest.id, decision, reviewNote.trim());
            setSpace(result.space);
            setSettingsNotice(result.message);
            setReviewNote("");
        } catch (err: any) {
            setError(err?.message || "Could not review dissolution.");
        } finally {
            setSpaceActionBusy(false);
        }
    }

    async function handleUnlock(e: React.FormEvent) {
        e.preventDefault();
        if (!space) return;
        if (inviteCode.trim().length < MIN_UNLOCK_CODE_LENGTH) return;
        setJoining(true);
        setError(null);
        try {
            const updated = await unlockSpace(space.id, inviteCode.trim());
            setSpace(updated);
            syncSpaceDrafts(updated);
            setInviteCode("");
        } catch (err: any) {
            setError(spaceUnlockErrorMessage(err?.message));
        } finally {
            setJoining(false);
        }
    }

    async function handleAccessSave(regenerateInvite = false) {
        if (!space) return;
        setSpaceActionBusy(true);
        setError(null);
        setCopiedAccess(null);
        try {
            const updated = await updateSpace(space.id, {
                accessMode: accessModeDraft,
                lockedReason: lockedReasonDraft,
                inviteExpiresAt: inviteExpiresAtDraft ? new Date(inviteExpiresAtDraft).toISOString() : null,
                regenerateInvite,
                revokeExistingAccess: regenerateInvite && revokeExistingAccessDraft,
            });
            setSpace(updated);
            syncSpaceDrafts(updated);
            if (updated.inviteCode) setRevealedAccessCode(updated.inviteCode);
            if (updated.accessMode === "open") setRevealedAccessCode("");
            setRevokeExistingAccessDraft(false);
        } catch (err: any) {
            setError(err?.message || "Could not update space access.");
        } finally {
            setSpaceActionBusy(false);
        }
    }

    async function handleIdentitySave() {
        if (!space) return;
        setSpaceActionBusy(true);
        setError(null);
        try {
            const updated = await updateSpace(space.id, {
                title: titleDraft.trim() || space.title,
                desc: descriptionDraft.trim(),
                accentColor: cleanAccentColor(accentColorDraft),
            });
            setSpace(updated);
            syncSpaceDrafts(updated);
            setSettingsNotice("Saved.");
        } catch (err: any) {
            setError(err?.message || "Could not update space identity.");
        } finally {
            setSpaceActionBusy(false);
        }
    }

    async function handleCopyAccess(kind: "code" | "link") {
        if (!space || !revealedAccessCode) return;
        const value = kind === "code"
            ? revealedAccessCode
            : `${window.location.origin}/spaces/${encodeURIComponent(space.id)}?code=${encodeURIComponent(revealedAccessCode)}`;

        try {
            await navigator.clipboard?.writeText(value);
            setCopiedAccess(kind);
            window.setTimeout(() => setCopiedAccess((current) => (current === kind ? null : current)), 1800);
        } catch {
            setError("Could not copy. Select the code manually.");
        }
    }

    if (loading) {
        return (
            <div className="h-screen bg-black text-white flex flex-col">
                <main className="flex-1 overflow-y-auto p-6">
                    <div className="mx-auto max-w-4xl space-y-4">
                        <div className="h-28 animate-pulse rounded-lg border border-neutral-800 bg-neutral-950" />
                        <TabLoader />
                    </div>
                </main>
                <FooterNav />
            </div>
        );
    }

    if (error && !space) {
        return (
            <div className="h-screen bg-black text-white flex flex-col">
                <main className="flex-1 grid place-items-center p-6">
                    <div className="max-w-md rounded-lg border border-red-900 bg-red-950/30 p-6 text-center">
                        <h1 className="text-lg font-semibold text-red-200">Space unavailable</h1>
                        <p className="mt-2 text-sm text-red-300">{error}</p>
                        <button
                            onClick={goBackToSource}
                            className="mt-4 rounded-md bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600"
                        >
                            Back to {backTarget.label}
                        </button>
                    </div>
                </main>
                <FooterNav />
            </div>
        );
    }

    if (!space) return null;

    const tabs = baseTabs;
    const isDefaultSpace = space.id === "blackspace";
    const isAuthorityView = Boolean(authority?.isPresence || (space.canManage && !space.isJoined));
    const canViewSpace = Boolean(space.canView !== false || space.canEnter || isAuthorityView);
    const canOpenModeration = Boolean(!space.isArchived && authority?.canModerate);
    const canManageSpace = Boolean(!space.isArchived && (space.canManage || authority?.canManageSpace || authority?.isCreator));
    const canOpenSpaceMenu = Boolean(canOpenModeration || canManageSpace);
    const canViewMemberSections = Boolean(
        isAuthorityView ||
        space.isJoined ||
        (space.isArchived && canViewSpace) ||
        authority?.isCreator ||
        authority?.isCoCreator ||
        authority?.canModerate ||
        authority?.canManageSpace
    );
    const canDeleteSpaceDirectly = Boolean(!space.isArchived && space.canDeleteDirectly);
    const canRequestDissolution = Boolean(!space.isArchived && space.canRequestDissolution);
    const accessText = accessLanguage(space.accessMode || "open");
    const draftAccessText = accessLanguage(accessModeDraft || "open");
    const entryOptions = [
        { value: "open", label: "Open", helper: "Anyone can enter", icon: Shield },
        { value: "locked", label: "Locked", helper: "Code unlock", icon: Lock },
        { value: "private", label: "Invite only", helper: "Approved entry", icon: UserPlus },
        ...(authority?.isPresence ? [{ value: "sealed", label: "Sealed", helper: "Private room", icon: ShieldCheck }] : []),
    ];
    const accessCodeReady = inviteCode.trim().length >= MIN_UNLOCK_CODE_LENGTH;
    const accentColor = cleanAccentColor(space.accentColor);
    const spaceSummary = [
        space.privateStats ? "Private members" : `${space.members || "0"} Members`,
        `${space.online || "0"} Live`,
        `${space.activity?.posts || 0} Posts`,
        `${space.activity?.upcomingEvents || 0} Events`,
        accessText.label,
    ];

    return (
        <div
            className="bs-mobile-space-detail h-screen bg-black text-white flex flex-col overflow-hidden"
            style={{
                "--bs-space-accent": accentColor,
                "--bs-space-accent-soft": alphaHex(accentColor, "30"),
                "--bs-space-accent-border": alphaHex(accentColor, "88"),
            } as React.CSSProperties}
        >
            <header className="bs-community-detail-header bs-space-detail-header border-b border-white/10 bg-black/95 backdrop-blur-xl">
                <div className="mx-auto max-w-6xl px-4 pt-3 sm:px-6 sm:pt-5">
                    <div className="bs-space-detail-top flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <button onClick={goBackToSource} className="bs-space-header-command mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full text-neutral-500 hover:bg-white/5 hover:text-white" aria-label={`Back to ${backTarget.label}`}>
                                <ArrowLeft size={18} />
                            </button>
                            <div
                                className="bs-space-room-mark grid h-12 w-12 shrink-0 place-items-center rounded-xl border text-base font-black text-white"
                                style={{
                                    borderColor: alphaHex(accentColor, "88"),
                                    background: `linear-gradient(135deg, ${alphaHex(accentColor, "55")}, #090909)`,
                                }}
                            >
                                {space.title.slice(0, 1).toUpperCase()}
                            </div>
                            <div className="bs-space-room-copy min-w-0">
                                <h1 className="bs-message-page-title truncate">{space.title}</h1>
                                <p className="bs-space-room-description mt-0.5 line-clamp-1 max-w-2xl text-sm text-neutral-500">{space.desc || `A community by ${space.by || "Blackspace"}.`}</p>
                                <p className="bs-space-detail-meta mt-1 flex max-w-[78vw] items-center gap-1.5 overflow-x-auto whitespace-nowrap text-xs font-bold text-neutral-500 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    {spaceSummary.map((item, index) => (
                                        <React.Fragment key={item}>
                                            {index > 0 && <span aria-hidden="true">&bull;</span>}
                                            <span>{item}</span>
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1.5">
                            {canViewSpace && canOpenSpaceMenu && <button onClick={() => setMoreOpen(true)} aria-expanded={moreOpen} className="bs-space-header-command grid h-9 w-9 place-items-center rounded-full border border-white/10 text-neutral-400 hover:bg-white/5 hover:text-white" aria-label="More Space actions"><Ellipsis size={17} /></button>}
                            {!space.isArchived && !isAuthorityView && <button
                                onClick={toggleMembership}
                                disabled={joining || (space.isJoined && isDefaultSpace) || Boolean(space.requiresInvite)}
                                className={`bs-space-membership-action h-9 rounded-full px-3 text-xs font-black transition disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 ${space.isJoined ? "border border-white/15 bg-neutral-950 text-neutral-200 hover:bg-white/5" : "bg-white text-black hover:bg-cyan-100"}`}
                            >
                                {joining ? "Working" : space.requiresInvite ? "Unlock" : space.isJoined ? (isDefaultSpace ? "Home" : "Leave") : "Join"}
                            </button>}
                        </div>
                    </div>

                    {canViewSpace && (
                        <nav className="bs-space-detail-tabs mt-4 flex gap-5 overflow-x-auto" aria-label="Space sections">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => selectTab(tab.id)}
                                    aria-current={activeTab === tab.id ? "page" : undefined}
                                    className={`relative inline-flex shrink-0 items-center gap-2 border-b-2 px-1 pb-3 text-sm font-black transition ${activeTab === tab.id ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-200"}`}
                                >
                                    <tab.icon size={15} /> {tab.label}
                                </button>
                            ))}
                        </nav>
                    )}
                </div>
            </header>

            <main ref={mainRef} className="flex-1 overflow-y-auto">
                {activeTab === "manage" && canManageSpace && (
                <div className="bs-space-manage-page mx-auto max-w-6xl p-4 pb-24 sm:px-6">
                    <section aria-labelledby="manage-space-title" className="bs-space-primary-feed bs-space-manage-modal bs-space-manage-inline rounded-2xl border border-neutral-800 bg-[#080808] shadow-2xl">
                        <header className="bs-space-manage-header flex shrink-0 items-start justify-between gap-4 border-b border-neutral-900 px-5 py-5 md:px-7">
                            <div>
                                <h2 id="manage-space-title" className="sr-only">Space Controls</h2>
                                <span className="bs-space-panel-pill bs-space-panel-pill-lg"><ShieldCheck size={14} /> Space Controls</span>
                            </div>
                        </header>
                        <div className="bs-space-manage-body px-4 py-4 md:px-6 md:py-6">
                        {error && <div className="bs-space-moderation-toast is-error mb-4">{error}</div>}
                        {settingsNotice && <div className="bs-space-moderation-toast is-success mb-4">{settingsNotice}</div>}
                            <div className="bs-space-control-shell rounded-2xl border border-neutral-800 bg-neutral-950/50 p-4 md:p-5">
                                {space.accessMode !== "open" && (
                                    <div className="bs-space-control-intro flex flex-col gap-3 md:flex-row md:items-start md:justify-end">
                                        {revealedAccessCode ? (
                                            <div className="flex max-w-full flex-col items-start gap-1 md:items-end">
                                                <div className="flex max-w-full flex-wrap justify-start gap-2 md:justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCopyAccess("code")}
                                                        className={`inline-flex max-w-full items-center gap-2 rounded-md border px-3 py-2 text-xs font-bold transition-colors ${
                                                            copiedAccess === "code"
                                                                ? "border-emerald-700 bg-emerald-950/40 text-emerald-200"
                                                                : "border-neutral-700 bg-black text-cyan-100 hover:border-cyan-500"
                                                        }`}
                                                        title={`Copy ${accessText.codeLabel.toLowerCase()}`}
                                                    >
                                                        {copiedAccess === "code" ? <Check size={14} /> : <Copy size={14} />}
                                                        <span className="truncate font-mono tracking-wide">{revealedAccessCode}</span>
                                                        <span className="text-neutral-500">{copiedAccess === "code" ? accessText.copied : "Copy"}</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCopyAccess("link")}
                                                        className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-bold transition-colors ${
                                                            copiedAccess === "link"
                                                                ? "border-emerald-700 bg-emerald-950/40 text-emerald-200"
                                                                : "border-violet-800/70 bg-violet-950/20 text-violet-100 hover:border-violet-500"
                                                        }`}
                                                    >
                                                        {copiedAccess === "link" ? <Check size={14} /> : <KeyRound size={14} />}
                                                        {copiedAccess === "link" ? accessText.copiedLink : "Copy link"}
                                                    </button>
                                                </div>
                                                <span className="text-[11px] font-semibold text-amber-300">Shown once. Save it before leaving this page.</span>
                                            </div>
                                        ) : (
                                            <div className="rounded-md border border-neutral-800 bg-black/60 px-3 py-2 text-right">
                                                <div className="font-mono text-xs font-bold text-neutral-300">
                                                    Code ending {space.inviteCodeHint ? `****${space.inviteCodeHint}` : "hidden"}
                                                </div>
                                                <div className="mt-1 text-[11px] text-neutral-500">Rotate to reveal a new code.</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <details className="bs-space-control-section mt-4">
                                    <summary className="bs-space-control-summary">
                                        <span>Basics</span>
                                    </summary>
                                    <div className="bs-space-control-body">
                                        <div className="bs-space-basics-grid">
                                            <label className="bs-space-manage-field">
                                                <span>Space name</span>
                                                <div className="bs-space-manage-field-control">
                                                    <ScrollText size={15} />
                                                    <input
                                                        value={titleDraft}
                                                        onChange={(e) => setTitleDraft(e.target.value.slice(0, 80))}
                                                        className="bs-space-manage-plain-input"
                                                        placeholder="Space name"
                                                    />
                                                </div>
                                            </label>
                                            <label className="bs-space-manage-field">
                                                <span>Description</span>
                                                <div className="bs-space-manage-field-control bs-space-manage-description-control">
                                                    <input
                                                        value={descriptionDraft}
                                                        onChange={(e) => setDescriptionDraft(e.target.value.slice(0, 280))}
                                                        className="bs-space-manage-plain-input"
                                                        placeholder="What lives here?"
                                                    />
                                                </div>
                                            </label>
                                        </div>
                                        <div className="bs-space-colour-row">
                                            <span className="bs-space-colour-label">Colour</span>
                                            <div className="bs-space-colour-options">
                                                {SPACE_ACCENTS.map((color) => (
                                                    <button
                                                        key={color}
                                                        type="button"
                                                        onClick={() => setAccentColorDraft(color)}
                                                        className={`bs-space-colour-dot ${cleanAccentColor(accentColorDraft) === color ? "is-selected" : ""}`}
                                                        style={{ background: color, boxShadow: cleanAccentColor(accentColorDraft) === color ? `0 0 18px ${color}66` : undefined }}
                                                        title={color}
                                                    />
                                                ))}
                                                <input
                                                    type="color"
                                                    value={cleanAccentColor(accentColorDraft)}
                                                    onChange={(e) => setAccentColorDraft(e.target.value)}
                                                    className="bs-space-colour-native"
                                                    title="Custom colour"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleIdentitySave}
                                                disabled={spaceActionBusy}
                                                className="bs-space-control-save"
                                                style={{
                                                    "--bs-space-draft-accent": cleanAccentColor(accentColorDraft),
                                                    borderColor: alphaHex(cleanAccentColor(accentColorDraft), "aa"),
                                                    background: `linear-gradient(135deg, ${cleanAccentColor(accentColorDraft)}, ${alphaHex(cleanAccentColor(accentColorDraft), "cc")})`,
                                                    boxShadow: `0 0 22px ${alphaHex(cleanAccentColor(accentColorDraft), "28")}`,
                                                } as React.CSSProperties}
                                            >
                                                <Check size={15} />
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                </details>

                                <details className="bs-space-control-section mt-4">
                                    <summary className="bs-space-control-summary">
                                        <span>Entry</span>
                                    </summary>
                                    <div className="bs-space-control-body">
                                        <div className="bs-space-entry-options">
                                            {entryOptions.map((option) => {
                                                const Icon = option.icon;
                                                const selected = accessModeDraft === option.value;
                                                return (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        onClick={() => setAccessModeDraft(option.value)}
                                                        aria-pressed={selected}
                                                        className="bs-space-entry-option"
                                                    >
                                                        <Icon size={15} />
                                                        <span>
                                                            <strong>{option.label}</strong>
                                                            <small>{option.helper}</small>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="bs-space-entry-actions">
                                            <button
                                                type="button"
                                                onClick={() => handleAccessSave(false)}
                                                disabled={spaceActionBusy}
                                                className="bs-space-control-save"
                                            >
                                                <ShieldCheck size={15} />
                                                Save entry
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleAccessSave(true)}
                                                disabled={spaceActionBusy || accessModeDraft === "open"}
                                                className="bs-space-control-ghost"
                                                title={`Rotate ${draftAccessText.codeLabel.toLowerCase()}`}
                                            >
                                                <RefreshCw size={15} />
                                                Rotate
                                            </button>
                                        </div>

                                {accessModeDraft !== "open" && (
                                    <div className="mt-3 space-y-3">
                                        <label className="bs-space-manage-field">
                                            <span>Code expiry</span>
                                            <div className="bs-space-manage-field-control">
                                                <input
                                                    type="datetime-local"
                                                    value={inviteExpiresAtDraft}
                                                    min={toDateTimeLocal(new Date(Date.now() + 60_000).toISOString())}
                                                    onChange={(event) => setInviteExpiresAtDraft(event.target.value)}
                                                    className="bs-space-manage-plain-input"
                                                />
                                            </div>
                                            <small className="text-xs text-neutral-500">Optional. Existing members keep access; new code entries stop at this time.</small>
                                        </label>
                                        <label className="bs-space-entry-toggle">
                                            <input
                                                type="checkbox"
                                                checked={revokeExistingAccessDraft}
                                                onChange={(e) => setRevokeExistingAccessDraft(e.target.checked)}
                                                className="h-4 w-4 accent-cyan-600"
                                            />
                                            <span>
                                                <span className="block font-bold text-white">Rotate as a clean door</span>
                                                <span className="text-xs text-neutral-500">Clears remembered unlocks when the code is rotated.</span>
                                            </span>
                                        </label>
                                    </div>
                                )}

                                {Boolean(space.accessGrants?.length) && (
                                    <div className="mt-4 rounded-lg border border-neutral-800 bg-black/50 p-3">
                                        <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-neutral-400">
                                            <History size={14} />
                                            Recent code access
                                        </div>
                                        <div className="grid gap-2 md:grid-cols-2">
                                            {space.accessGrants?.slice(0, 6).map((grant) => (
                                                <div key={`${grant.userId}-${grant.grantedAt}`} className="flex items-center justify-between gap-3 rounded-md border border-neutral-900 bg-neutral-950/80 px-3 py-2 text-xs">
                                                    <span className="truncate font-bold text-neutral-200">@{grant.username}</span>
                                                    <span className="shrink-0 text-neutral-500">{formatWhen(grant.grantedAt)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                    </div>
                                </details>

                                {!isDefaultSpace && (
                                    <details className="bs-space-control-section mt-4">
                                        <summary className="bs-space-control-summary">
                                            <span>Exit</span>
                                        </summary>
                                        <section className="bs-space-control-body">
                                        <div className="flex items-start gap-3">
                                            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-amber-700/40 bg-amber-950/40 text-amber-200"><History size={17} /></div>
                                            <div>
                                                <h3 className="font-black text-white">Continuity and dissolution</h3>
                                                <p className="mt-1 text-sm leading-6 text-neutral-400">Leaving transfers responsibility. Dissolution ends activity and automatically preserves the Space as a read-only archive.</p>
                                            </div>
                                        </div>

                                        {space.dissolutionRequest && (
                                            <div className={`mt-4 rounded-xl border p-3 text-sm ${space.dissolutionRequest.status === "rejected" ? "border-red-900/60 bg-red-950/20 text-red-100" : "border-cyan-900/50 bg-cyan-950/15 text-cyan-100"}`}>
                                                <div className="font-black capitalize">Dissolution {space.dissolutionRequest.status}</div>
                                                <p className="mt-1 text-neutral-400">{space.dissolutionRequest.reviewNote || space.dissolutionRequest.reason}</p>
                                                {space.dissolutionRequest.status === "rejected" && <p className="mt-2 text-xs font-bold text-neutral-500">The owner may appeal through the existing Blackbox outside Spaces.</p>}
                                            </div>
                                        )}

                                        {(canDeleteSpaceDirectly || (canRequestDissolution && space.dissolutionRequest?.status !== "pending")) && (
                                            canDeleteSpaceDirectly ? (
                                                <div className="mt-4 rounded-xl border border-red-900/50 bg-red-950/15 p-3">
                                                    <p className="text-sm font-black text-red-100">Delete unused Space</p>
                                                    <p className="mt-1 text-xs leading-5 text-neutral-400">{space.canDeleteWithoutReview ? "No other member is attached here, so review is not required. This Space will disappear rather than become an archive." : "This removes the Space directly. Peer-protected rooms remain unavailable."}</p>
                                                    {deleteConfirm && <p className="mt-3 text-xs font-bold text-red-200">Confirm deletion. This cannot be restored from the Spaces interface.</p>}
                                                    <button type="button" onClick={handleDeleteSpace} disabled={spaceActionBusy} className="mt-3 inline-flex h-10 items-center gap-2 rounded-full border border-red-800 bg-red-950/40 px-4 text-xs font-black text-red-100 hover:bg-red-900/40 disabled:opacity-40"><Trash2 size={14} /> {deleteConfirm ? "Confirm delete" : "Delete Space"}</button>
                                                </div>
                                            ) : (
                                                <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                                                    <label><span className="mb-1 block text-xs font-bold text-neutral-400">Reason for dissolution</span><textarea value={dissolutionReason} onChange={(event) => setDissolutionReason(event.target.value.slice(0, 1000))} rows={3} className="w-full resize-none rounded-xl border border-neutral-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-600" placeholder="Explain why this community should end..." /></label>
                                                    <button type="button" onClick={handleDissolutionRequest} disabled={spaceActionBusy || dissolutionReason.trim().length < 12} className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-amber-700/60 bg-amber-950/30 px-4 text-sm font-black text-amber-100 hover:bg-amber-900/30 disabled:opacity-40"><History size={15} /> Request dissolution</button>
                                                </div>
                                            )
                                        )}

                                        {(authority?.isPatron || authority?.isPresence) && space.dissolutionRequest?.status === "pending" && (
                                            <div className="mt-4 rounded-xl border border-fuchsia-900/50 bg-fuchsia-950/15 p-3">
                                                <p className="text-xs font-black uppercase tracking-wider text-fuchsia-200">Review</p>
                                                <p className="mt-2 text-sm text-neutral-300">{space.dissolutionRequest.reason}</p>
                                                <label className="mt-3 block"><span className="mb-1 block text-xs font-bold text-neutral-400">Message to owner</span><textarea value={reviewNote} onChange={(event) => setReviewNote(event.target.value.slice(0, 1000))} rows={2} className="w-full resize-none rounded-xl border border-neutral-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-600" placeholder="Explain your decision. This will appear in Alerts." /></label>
                                                <div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => handleDissolutionReview("rejected")} disabled={spaceActionBusy || reviewNote.trim().length < 3} className="rounded-full border border-neutral-700 px-4 py-2 text-xs font-black text-neutral-200 disabled:opacity-40">Reject</button><button type="button" onClick={() => handleDissolutionReview("approved")} disabled={spaceActionBusy || reviewNote.trim().length < 3} className="rounded-full bg-white px-4 py-2 text-xs font-black text-black disabled:opacity-40">Approve and archive</button></div>
                                            </div>
                                        )}
                                        </section>
                                    </details>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
                )}

                {(activeTab !== "manage" || !canManageSpace) && (
                <div className="mx-auto max-w-6xl p-4 pb-24 sm:px-6">
                    {space.isArchived && (
                        <section className="mb-5 rounded-2xl border border-amber-700/25 bg-amber-500/[0.045] px-4 py-3">
                            <p className="text-sm font-semibold leading-6 text-neutral-300">This conversation is preserved exactly as it stood when the Space was archived.</p>
                        </section>
                    )}
                    {error && space && canViewSpace && (
                        <div role="alert" className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-100">
                            <span>{error}</span>
                            <button type="button" onClick={() => setError(null)} className="shrink-0 rounded-full p-1 text-red-200/70 hover:bg-red-400/10 hover:text-white" aria-label="Dismiss message"><X size={14} /></button>
                        </div>
                    )}
                    {!canViewSpace ? (
                        <section
                            className="bs-space-unlock-shell mx-auto"
                            role="dialog"
                            aria-labelledby="space-unlock-title"
                            aria-describedby="space-unlock-copy"
                        >
                            <div className="bs-space-unlock-aura" aria-hidden="true" />
                            <div className="bs-space-unlock-head">
                                <div
                                    className="bs-space-unlock-mark"
                                    style={{
                                        borderColor: alphaHex(accentColor, "88"),
                                        background: `linear-gradient(135deg, ${alphaHex(accentColor, "50")}, rgba(4, 7, 13, 0.96))`,
                                    }}
                                >
                                    {space.title.slice(0, 1).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <div className="bs-space-unlock-kicker">
                                        <Lock size={12} />
                                        <span>{accessText.gateBadge}</span>
                                    </div>
                                    <h2 id="space-unlock-title" className="bs-space-unlock-title">{space.title}</h2>
                                    <p id="space-unlock-copy" className="bs-space-unlock-copy">{accessText.gateCopy}</p>
                                </div>
                            </div>

                            {error && (
                                <div role="alert" className="bs-space-unlock-error">
                                    <Shield size={15} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <form onSubmit={handleUnlock} className="bs-space-unlock-form">
                                <label className="bs-space-unlock-field">
                                    <span>{accessText.codeLabel}</span>
                                    <div className="bs-space-unlock-input-shell">
                                        <KeyRound size={17} />
                                        <input
                                            value={inviteCode}
                                            onChange={(e) => {
                                                if (error) setError(null);
                                                setInviteCode(e.target.value.toUpperCase().slice(0, 40));
                                            }}
                                            className="bs-space-unlock-input"
                                            placeholder="Enter access code"
                                            autoComplete="one-time-code"
                                            inputMode="text"
                                            spellCheck={false}
                                            autoFocus
                                        />
                                    </div>
                                </label>
                                <div className="bs-space-unlock-actions">
                                    <button type="button" onClick={goBackToSource} className="bs-space-unlock-cancel">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={joining || !accessCodeReady} className="bs-space-unlock-submit">
                                        <Lock size={16} />
                                        {joining ? "Checking" : accessText.action}
                                    </button>
                                </div>
                            </form>
                        </section>
                    ) : (
                        <Suspense fallback={<TabLoader />}>
                            {(activeTab === "posts" || (activeTab === "moderation" && !canOpenModeration) || (activeTab === "manage" && !canManageSpace)) && (
                                <div className="bs-space-primary-feed">
                                    <PostsTab spaceId={space.id} space={space} />
                                </div>
                            )}
                            {activeTab === "events" && !canViewMemberSections && <JoinRequiredPanel title="events" />}
                            {activeTab === "events" && canViewMemberSections && (
                                <div className="bs-space-primary-feed bs-space-events-rail">
                                    <EventsTab spaceId={space.id} space={space} />
                                </div>
                            )}
                            {activeTab === "library" && !canViewMemberSections && <JoinRequiredPanel title="resources" />}
                            {activeTab === "library" && canViewMemberSections && (
                                <div className="bs-space-primary-feed bs-space-library-rail">
                                    <MaterialsTab spaceId={space.id} space={space} />
                                </div>
                            )}
                            {activeTab === "info" && (
                                <div className="bs-space-info-tab space-y-7">
                                    <SpaceAbout space={space} />
                                    <section aria-labelledby="space-members-title">
                                        <h3 id="space-members-title" className="mb-3 text-sm font-black text-white">Members</h3>
                                        <MembersTab spaceId={space.id} space={space} />
                                    </section>
                                </div>
                            )}
                            {activeTab === "moderation" && canOpenModeration && (
                                <div className="bs-space-primary-feed">
                                    <ModerationTab spaceId={space.id} space={space} />
                                </div>
                            )}
                        </Suspense>
                    )}
                </div>
                )}
            </main>

            {moreOpen && canViewSpace && canOpenSpaceMenu && (
                <div className="fixed inset-0 z-[75] flex items-end bg-black/70 p-3 backdrop-blur-sm sm:items-start sm:justify-end sm:p-5 sm:pt-20" onClick={() => setMoreOpen(false)}>
                    <div className="bs-space-action-menu w-full rounded-2xl border border-white/10 bg-neutral-950 p-3 shadow-2xl sm:max-w-sm" role="dialog" aria-modal="true" aria-label="Space actions" onClick={(event) => event.stopPropagation()}>
                        <div className="mb-2 flex items-center justify-between px-2 py-1"><h2 className="text-sm font-black text-white">{space.title}</h2><button type="button" onClick={() => setMoreOpen(false)} className="p-2 text-neutral-500" aria-label="Close Space actions"><X size={17} /></button></div>
                        {canOpenModeration && <button type="button" onClick={() => { setMoreOpen(false); selectTab("moderation"); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold text-neutral-200 hover:bg-white/5"><Shield size={17} className="text-neutral-500" /> Moderation</button>}
                        {canManageSpace && <button type="button" onClick={() => { setMoreOpen(false); clearLifecycleDrafts(); selectTab("manage"); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold text-neutral-200 hover:bg-white/5"><ShieldCheck size={17} className="text-neutral-500" /> Manage Space</button>}
                    </div>
                </div>
            )}

            {continuityOpen && space && (
                <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm md:items-center md:p-5" onMouseDown={(event) => event.target === event.currentTarget && closeContinuity()}>
                    <section role="dialog" aria-modal="true" aria-labelledby="creator-exit-title" className="w-full max-w-xl rounded-t-[28px] border border-neutral-800 bg-[#080808] p-5 shadow-2xl md:rounded-[28px] md:p-6">
                        <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">Community continuity</p><h2 id="creator-exit-title" className="mt-1 text-2xl font-black text-white">Leave {space.title}</h2></div><button type="button" onClick={closeContinuity} aria-label="Close creator exit" className="rounded-full border border-neutral-800 p-2 text-neutral-400"><X size={17} /></button></div>
                        {space.canDeleteDirectly ? (
                            <div className="mt-5">
                                <p className="text-sm leading-6 text-neutral-400">No other member is attached here. This Space can be deleted directly instead of archived.</p>
                                {deleteConfirm && <p className="mt-3 text-xs font-bold text-red-200">Confirm deletion. This cannot be restored from the Spaces interface.</p>}
                                <button type="button" onClick={handleDeleteSpace} disabled={spaceActionBusy} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-red-800 bg-red-950/40 px-5 py-3 text-sm font-black text-red-100 disabled:opacity-40"><Trash2 size={15} />{deleteConfirm ? "Confirm delete" : "Delete Space"}</button>
                            </div>
                        ) : space.canRequestDissolution ? (
                            <div className="mt-5">
                                <p className="text-sm leading-6 text-neutral-400">Owner exit needs review when this Space still has community history or active members.</p>
                                <label className="mt-4 block text-sm font-bold text-white">Reason<textarea value={dissolutionReason} onChange={(event) => setDissolutionReason(event.target.value.slice(0, 1000))} rows={3} className="mt-2 w-full resize-none rounded-xl border border-neutral-800 bg-black px-3 py-3 text-white outline-none focus:border-amber-600" placeholder="Why should this Space be dissolved?" /></label>
                                <button type="button" onClick={handleDissolutionRequest} disabled={dissolutionReason.trim().length < 12 || spaceActionBusy} className="mt-5 w-full rounded-full border border-amber-700/60 bg-amber-950/30 px-5 py-3 text-sm font-black text-amber-100 disabled:opacity-40">Request dissolution</button>
                            </div>
                        ) : (
                            <div className="mt-5 rounded-2xl border border-neutral-800 bg-white/[0.03] p-4">
                                <p className="text-sm leading-6 text-neutral-300">{space.deletionUnavailableReason || "Assign another Owner before leaving this Space."}</p>
                            </div>
                        )}
                    </section>
                </div>
            )}

            <FooterNav />
        </div>
    );
}
